import React, { useState, useEffect, useContext } from 'react';

import { AssetTypes } from '@api/v4/assets/assetTypes';
import DetailsSection, { initializeTrumbowyg } from './detailsSection';
import CustomFieldsSection from './custom_fields/customFieldsSection';
import { CustomFieldsList } from './custom_fields/CustomFieldsList';
import AdvancedSection from './advanced/AdvancedSection';
import TemplatingSection from './templating/TemplatingSection';
import SectionBar, { SectionDetails } from './sectionBar';
import { AssetDetailsDispatch, FlattenedAttachment } from '../EditTabTypes';
import assetModalContext from '../asset_modal_context';

import './styles/_main.scss';

interface GenericFileEditFormScopedContext {
  state: {
    initialData: {
      assetKey: string;
      assetType: AssetTypes;
    };
    editState: {
      assetDescription: string;
      attachments: FlattenedAttachment[];
    };
  };
  dispatch: AssetDetailsDispatch;
}

const GenericFileEditForm = ({ includeAdvancedOptions }: { includeAdvancedOptions?: boolean }): JSX.Element => {
  const { state, dispatch }: GenericFileEditFormScopedContext = useContext(assetModalContext);
  const { assetDescription, attachments } = state.editState;

  const [textareaVisible, setTextareaVisible] = useState(true);
  const hasTemplateAttachmentKey = (): string => (
    attachments?.find((attachment: FlattenedAttachment) => attachment.extension === 'bftemplate')?.key || ''
  );

  const sections: SectionDetails[] = [{
    displayName: bfTranslate('Details'),
    content: (
      <DetailsSection />
    ),
    onActiveSection: (): void => setTextareaVisible(true)
  }];
  if (BFG.hasFeature('custom_fields')) {
    sections.push({
      tabClassName: 'custom-fields',
      displayName: bfTranslate('Custom Fields'),
      content: BFG.hasFeature('rollout_multi_value_custom_fields') ? <CustomFieldsList /> : <CustomFieldsSection />,
      onActiveSection: (): void => setTextareaVisible(false)
    });
  }

  if (state.initialData.assetType !== AssetTypes.Text) {
    /**
     * includeAdvancedOptions indicates whether anything more than
     * the custom thumbnail override setting should be displayed
     * in the advanced tab
     *
     * every asset type _except_ text assets should have the custom
     * thumbnail override setting available but text assets don't use
     * thumbnail in their card/overview displays so we shouldn't show
     * that setting. That would be the only setting in the advanced
     * tab for text assets so we just shouldn't show the advanced
     * tab for text assets at all
     */
    sections.push({
      displayName: bfTranslate('Advanced'),
      content: <AdvancedSection includeAdvancedOptions={includeAdvancedOptions} />,
      onActiveSection: (): void => setTextareaVisible(false)
    });
  }

  const templateAttachmentKey = hasTemplateAttachmentKey();
  if (templateAttachmentKey) {
    sections.push({
      displayName: bfTranslate('Templating'),
      content: <TemplatingSection attachmentKey={templateAttachmentKey} />,
      onActiveSection: (): void => setTextareaVisible(false),
      tabClassName: 'templating',
    });
  }

  useEffect(() => {
    if (textareaVisible) {
      initializeTrumbowyg(assetDescription, dispatch);
    } else {
      // if not on details tab, kill trumbowyg
      ($('#asset-description-textarea') as TrumbowygJquery).trumbowyg('destroy');
    }
  }, [textareaVisible]);

  return (
    <div className="edit-tab-main-pane two-thirds bf-scroll-element">
      <SectionBar sections={sections} />
    </div>
  );
};

export default GenericFileEditForm;
